import type { GameState } from '../game/GameState.ts';
import { EdgeLatch, doFlap, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

/**
 * Keyboard control. The baseline condition for every comparison in the study:
 * a human intending to flap, with only display and keyboard latency in the way.
 *
 * Key-down edges are latched rather than sampled, so a 5 ms tap between two
 * simulation ticks still produces exactly one flap.
 */
export class KeyboardController implements Controller {
  readonly id = 'keyboard';
  readonly label = 'Keyboard';

  private latch = new EdgeLatch();
  private attached = false;
  private pressCount = 0;
  private handler = (e: KeyboardEvent) => this.onKeyDown(e);

  constructor(private readonly keys: string[] = ['Space']) {}

  attach(): void {
    if (this.attached) return;
    window.addEventListener('keydown', this.handler);
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    window.removeEventListener('keydown', this.handler);
    this.latch.clear();
    this.attached = false;
  }

  reset(): void {
    this.latch.clear();
  }

  update(_state: GameState): ControllerOutput {
    const edge = this.latch.consume();
    if (!edge) return noFlap();
    return { flap: true, timestamp: edge.timestamp, metadata: { source: 'keyboard' } };
  }

  status(): Record<string, unknown> {
    return { keys: this.keys.join(', '), presses: this.pressCount };
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.keys.includes(e.code)) return;
    if (e.repeat) return;
    e.preventDefault();
    this.pressCount++;
    this.latch.set(performance.now(), { key: e.code });
  }
}

/** Re-exported so callers can build a "no input at all" controller. */
export class NullController implements Controller {
  readonly id = 'none';
  readonly label = 'No input';
  update(): ControllerOutput {
    return noFlap();
  }
}

export { doFlap };
